import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
    parseWidgetData,
    parseCarePlanUpsellData,
    parseCartRecommendation,
    parseCouponData,
    parseDoneInOneWidgets,
    parseUpdateAddressData,
    parseCheckoutWidgets,
    parseLabsCheckoutWidgets,
    parseInsuranceBottomSheet,
    extractSkuIds,
    getCartItemsWidget,
} from "./parser"

import { handleAuthentication } from "@containers/App/reducer"
import { setRehashFlag } from "@containers/App/ABExperimentReducer/reducer.js"
import { getAssetPath } from "@commonUtils"
import { showSnackBar } from "@containers/App/reducer"
import GAEvents from "./GAEvent.js"

const initialState = {
    isFetching: true,
    isFetched: false,
    error: null,
    cartId: null,
    cartUpsellToken: null,
    widgets: {},
    recommendationWidget: [],
    recommendationLoading: false,
    listOfCoupons: [],
    isListOfCouponsFetched: false,
    isListOfCouponsFetching: false,
    listOfCouponError: null,
    doneInOneDialogWidgets: {},
    updateAddressDialogBox: {},
    outOfStockItems: {},
    isCouponApplying: false,
    analyticsData: {},
    errorMessage: "",
    autoAppliedPrescriptionDialog: {},
    isExistingRxValidForOrder: false,
    checkoutAPIPending: false,
    shouldFetchLabsRecommendations: false,
    showLoader: false,
    mixPanelData: null,
    insuranceBottomSheetData: null,
}

export const cartPageSlice = createSlice({
    name: "cartPage",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
        resetCouponError: (state, action) => {
            state.listOfCouponError = action.payload
        },
        updateCartPageData: (state, action) => {
            const cartType = action.payload.cartType
            if (cartType === "labs") {
                state.shouldFetchLabsRecommendations = true
            } else {
                state.cartUpsellToken = action?.payload?.cart_upsell_token
            }
            state.widgets = parseWidgetData(action?.payload)
            state.analyticsData = action?.payload?.analytics_data
            state.widgets["carePlanUpsell"] = parseCarePlanUpsellData(action.payload?.care_plan_info)
        },
        updateDoneInOneWidget: (state, action) => {
            state.doneInOneDialogWidgets["listOfItemsOnCart"] = parseDoneInOneWidgets(action?.payload)?.[
                "listOfItemsOnCart"
            ]
        },
        setShowLoader: (state, action) => {
            state.showLoader = action.payload
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchCartData.fulfilled, (state, action) => {
            state.isFetching = false
            state.isFetched = true
            state.error = false
            state.cartId = action?.payload?.cart_id
            state.cartUpsellToken = action?.payload?.cart_upsell_token
            state.widgets = parseWidgetData(action?.payload)
            state.analyticsData = action?.payload?.analytics_data
            state.widgets["carePlanUpsell"] = parseCarePlanUpsellData(action.payload?.care_plan_info)
            state.updateAddressDialogBox = parseUpdateAddressData(action?.payload?.update_address_dialog_data)
            state.mixPanelData = action?.payload?.analytics_data_mixpanel
        }),
            builder.addCase(fetchCartData.pending, (state) => {
                state.isFetching = true
                state.isFetched = false
                state.error = false
            }),
            builder.addCase(fetchCartData.rejected, (state) => {
                state.isFetching = false
                state.error = true
                state.isFetched = false
            }),
            builder.addCase(fetchCartRecommendation.fulfilled, (state, action) => {
                state.recommendationWidget = parseCartRecommendation(action?.payload?.widgets)
                state.recommendationLoading = false
                state.shouldFetchLabsRecommendations = false
            }),
            builder.addCase(fetchCartRecommendation.pending, (state) => {
                state.recommendationLoading = true
            }),
            builder.addCase(fetchCartRecommendation.rejected, (state) => {
                state.recommendationLoading = false
            }),
            builder.addCase(fetchCoupons.pending, (state) => {
                state.isListOfCouponsFetched = false
                state.isListOfCouponsFetching = true
            }),
            builder.addCase(fetchCoupons.fulfilled, (state, action) => {
                state.isListOfCouponsFetched = true
                state.isListOfCouponsFetching = false
                state.listOfCoupons = parseCouponData(action?.payload?.widgets)
            }),
            builder.addCase(removeCoupon.fulfilled, (state, action) => {
                state.cartUpsellToken = action?.payload?.cart_upsell_token
                state.widgets = parseWidgetData(action?.payload)
                state.analyticsData = action?.payload?.analytics_data
                state.widgets["carePlanUpsell"] = parseCarePlanUpsellData(action.payload?.care_plan_info)
            }),
            builder.addCase(refreshPageData.fulfilled, (state, action) => {
                const cartType = action.payload.cartType
                if (cartType === "labs") {
                    state.shouldFetchLabsRecommendations = true
                } else {
                    state.cartUpsellToken = action?.payload?.cart_upsell_token
                }
                state.widgets = parseWidgetData(action?.payload)
                state.analyticsData = action?.payload?.analytics_data
                state.widgets["carePlanUpsell"] = parseCarePlanUpsellData(action.payload?.care_plan_info)
            }),
            builder.addCase(applyCoupon.pending, (state) => {
                state.isCouponApplying = true
            }),
            builder.addCase(applyCoupon.fulfilled, (state) => {
                state.isCouponApplying = false
            }),
            builder.addCase(applyCoupon.rejected, (state, action) => {
                GAEvents?.onApplyCouponGA(action.payload?.message, "coupon error")
                state.isCouponApplying = false
                state.listOfCouponError = action.payload?.message
            }),
            builder.addCase(availUnavailNeucoins.fulfilled, (state, action) => {
                state.cartUpsellToken = action?.payload?.cart_upsell_token
                state.widgets = parseWidgetData(action?.payload)
                state.analyticsData = action?.payload?.analytics_data
                state.widgets["carePlanUpsell"] = parseCarePlanUpsellData(action.payload?.care_plan_info)
            }),
            builder.addCase(checkoutAPI.pending, (state) => {
                state.checkoutAPIPending = true
            }),
            builder.addCase(checkoutAPI.fulfilled, (state, action) => {
                const cartType = action?.payload?.cartType
                state.checkoutAPIPending = false
                state.widgets["checkoutButton"].redirectUrl = action.payload?.data?.target_url
                state.outOfStockItems =
                    cartType === "labs"
                        ? parseLabsCheckoutWidgets(action.payload?.data)
                        : parseCheckoutWidgets(action.payload?.data)
                state.isExistingRxValidForOrder = action?.payload?.data?.is_existing_rx_valid_for_order
                state.autoAppliedPrescriptionDialog = {
                    header: "Your existing prescriptions are valid for this order",
                    description: "You do not need to attach any new prescriptions to proceed with this order",
                    icon: getAssetPath("svgs/icons/correct_prescription_104px_primary.svg"),
                    primaryCta: { text: "Continue" },
                    targetUrl: action.payload?.data?.target_url,
                    height: "156px",
                    width: "156px",
                }
                // checkout API accepts a successCallback from cartPage which consists of actions that will be performed after we have received the data. We pass the updated state to this successCallback
                if (typeof action?.payload?.successCallback === "function")
                    action?.payload?.successCallback(state)
            }),
            builder.addCase(checkoutAPI.rejected, (state) => {
                state.checkoutAPIPending = false
            }),
            builder.addCase(fetchDoneInOneCartData.fulfilled, (state, action) => {
                state.doneInOneDialogWidgets = parseDoneInOneWidgets(action?.payload)
                state.doneInOneDialogWidgets["actionButton"] = {
                    label: "Add medicines to cart",
                    functionMap: "addToCartDoneInOne",
                }
            }),
            builder.addCase(mergeDoneInOneCart.fulfilled, (state) => {
                state.doneInOneDialogWidgets = {}
            })
        builder.addCase(fetchUpsellData.fulfilled, (state, action) => {
            if (action.payload && Object.keys(action.payload).length > 0) {
                // this is done to avoid the issue of insurance bottom sheet data being updated twice
                if (!action.payload.refetch) {
                    state.insuranceBottomSheetData = parseInsuranceBottomSheet(
                        action?.payload?.insurance_bottomsheet
                    )
                }
                state.widgets = { ...state.widgets, ...parseWidgetData(action?.payload) }
            }
        })
    },
})

export const fetchUpsellData = createAsyncThunk(
    "cartPage/fetchUpsellData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const cartType = getState().shellReducer?.cartType
        const { api } = extra
        const { upsellData, widgetData, refetch } = _arg
        const cartItemsWidget = getCartItemsWidget(widgetData)
        const cartSkus = extractSkuIds(cartItemsWidget)
        try {
            const apiUrl = "api/v4/carts/subscriptions/upsell_data"
            const payload = {
                types: cartType,
                upsell_data: upsellData,
                cart_skus: cartSkus,
            }
            const res = await api.post(apiUrl, payload)
            return Promise.resolve({ ...res.data, refetch })
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const fetchCartData = createAsyncThunk(
    "cartPage/fetchData",
    async (_arg = {}, { extra, rejectWithValue, dispatch, getState }) => {
        const { refetch = false } = _arg
        const { cartType } = getState().shellReducer
        const { api } = extra
        try {
            const apiUrl =
                cartType === "labs"
                    ? "api/v4/carts/details?types=labs&response_type=full"
                    : `api/v5/carts/details?types=pharmacy&response_type=full&cashback_availed=false`
            const res = await api.get(apiUrl)

            dispatch(setRehashFlag(res?.data?.rehash_data?.is_enabled))

            const { upsell_data: upsellData, widgets: widgetData } = res?.data ?? {}

            if (upsellData && widgetData?.length > 0) {
                dispatch(
                    fetchUpsellData({ upsellData: upsellData, widgetData: widgetData, refetch: refetch })
                )
            }

            if (res?.data?.message) {
                showSnackBar(dispatch, res?.data?.message)
            }
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const refreshPageData = createAsyncThunk(
    "cartPage/refreshPageData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        const successCallback = _arg?.successCallback
        const errorCallback = _arg?.errorCallback
        try {
            const apiUrl =
                cartType === "labs"
                    ? "api/v4/carts/details?types=labs&response_type=full"
                    : `api/v5/carts/details?types=pharmacy&response_type=full&cashback_availed=false`
            const res = await api.get(apiUrl)

            const { upsell_data: upsellData, widgets: widgetData } = res?.data ?? {}

            if (upsellData && widgetData?.length > 0) {
                dispatch(fetchUpsellData({ upsellData: upsellData, widgetData: widgetData }))
            }

            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res)
            return Promise.resolve({ ...res.data, cartType })
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (typeof errorCallback === "function") errorCallback()
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const fetchCartRecommendation = createAsyncThunk(
    "cartPage/fetchCartRecommendation",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const payload = _arg?.payload
        try {
            const res = await api.post(`api/v4/carts/recommendations`, {
                ...payload,
            })

            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const fetchCoupons = createAsyncThunk(
    "cartPage/fetchCoupons",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        try {
            const res = await api.get(`api/v4/carts/coupons?types=${cartType}`)
            return Promise.resolve(res?.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const removeCoupon = createAsyncThunk(
    "cartPage/removeCoupon",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        const successCallback = _arg.successCallback
        const errorCallback = _arg.errorCallback
        try {
            const apiUrl =
                cartType === "labs"
                    ? "api/v4/carts/coupons?types=labs&response_type=full"
                    : `api/v5/carts/coupons?types=pharmacy&response_type=full`
            const res = await api.delete(apiUrl)

            const { upsell_data: upsellData, widgets: widgetData } = res?.data ?? {}

            if (upsellData && widgetData?.length > 0) {
                dispatch(fetchUpsellData({ upsellData: upsellData, widgetData: widgetData }))
            }

            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res)
            return Promise.resolve(res?.data)
        } catch (err) {
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            const onServer = typeof window === "undefined"
            if (typeof errorCallback === "function") errorCallback()
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const applyCoupon = createAsyncThunk(
    "cartPage/applyCoupon",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        const successCallback = _arg?.successCallback
        const errorCallback = _arg?.errorCallback
        const couponCode = _arg?.couponCode
        try {
            const res = await api.put(
                `/api/v4/carts/coupons/${couponCode}/apply?types=${cartType}&response_type=full`
            )
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res)
            return Promise.resolve(res?.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (typeof errorCallback === "function") errorCallback(err)
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const mergeDoneInOneCart = createAsyncThunk(
    "cartPage/mergeDoneInOneCart",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const successCallback = _arg?.successCallback
        const errorCallback = _arg?.errorCallback
        try {
            const res = await api.post(`/api/v4/carts_merge`, { types: "done_in_one" })
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res)
            return Promise.resolve(res?.data)
        } catch (err) {
            if (typeof errorCallback === "function") errorCallback()
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const availUnavailNeucoins = createAsyncThunk(
    "cartPage/availUnavailNeucoins",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        const { tcpPointsAvailed, tcpPointsRedeemed } = _arg
        try {
            const apiUrl = `api/v5/carts/details?types=${cartType}&response_type=full&cashback_availed=false&tcp_points_availed=${tcpPointsAvailed}&tcp_points_redeemed=${tcpPointsRedeemed}`
            const res = await api.get(apiUrl)

            const { upsell_data: upsellData, widgets: widgetData } = res?.data ?? {}

            if (upsellData && widgetData?.length > 0) {
                dispatch(fetchUpsellData({ upsellData: upsellData, widgetData: widgetData }))
            }

            return Promise.resolve(res?.data)
        } catch (err) {
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const checkoutAPI = createAsyncThunk(
    "cartPage/checkoutData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        const successCallback = _arg?.successCallback
        const errorCallback = _arg?.successCallback
        try {
            const res = await api.get(`api/v4/carts/checkout?types=${cartType}`)
            // we pass the sucessCallback in action payload
            return Promise.resolve({ data: res?.data, successCallback, cartType })
        } catch (err) {
            if (typeof errorCallback === "function") errorCallback()
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const fetchDoneInOneCartData = createAsyncThunk(
    "cartPage/fetchDoneInOneCartData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const successCallback = _arg?.successCallback
        try {
            const res = await api.get(`api/v4/carts/items?types=done_in_one`)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res)
            return Promise.resolve(res?.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const checkBoxUpsellLabs = createAsyncThunk(
    "cartPage/checkBoxUpsellLabs",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { cartType } = getState().shellReducer
        const { api } = extra
        const { successCallback, queryParamKey, queryParamValue } = _arg
        try {
            const payload = {
                [queryParamKey]: queryParamValue,
            }
            const res = await api.put(`api/v4/carts/preferences?types=${cartType}`, payload)
            successCallback?.()
            return Promise.resolve(res?.data)
        } catch (err) {
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const cartPageReducer = cartPageSlice.reducer
export const { updateCartPageData, resetCouponError, updateDoneInOneWidget, reset, setShowLoader } =
    cartPageSlice.actions
